import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardFooter,
  CardDescription
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { UserProfile } from "@shared/types";
import { Template } from "@shared/schema";
import { TEMPLATE_CATEGORIES } from "@shared/constants";
import { formatDate } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/fetchWithAuth";
import { useOrganization } from "@/contexts/OrganizationContext";

interface TemplatesProps {
  user: UserProfile;
}

function TemplateUpdatedDate({ template }: { template: Template & { updated_at?: unknown } }) {
  // The mounted API returns database field names. Do not let an absent date
  // reach Intl.DateTimeFormat, which would display today's date instead.
  const value = template.updated_at !== undefined ? template.updated_at : template.updatedAt;
  if (!(typeof value === 'string' && value.trim()) && !(value instanceof Date)) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return <> • Last updated: {formatDate(date)}</>;
}

export default function Templates({ user }: TemplatesProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // New template form state
  const [templateName, setTemplateName] = useState("");
  const [templateCategory, setTemplateCategory] = useState(TEMPLATE_CATEGORIES.MEETING);
  const [templateContent, setTemplateContent] = useState("");
  
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentOrganizationId } = useOrganization();
  const effectiveOrganizationId = currentOrganizationId || user.organizationId;

  // Get templates
  const { data: templates, isLoading } = useQuery<Template[]>({
    queryKey: [`/api/templates?organizationId=${effectiveOrganizationId}`],
  });

  // Create template mutation
  const createTemplate = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('/api/templates', {
        method: 'POST',
        data: {
          name: templateName,
          category: templateCategory,
          content: JSON.stringify({ content: templateContent }),
          organizationId: effectiveOrganizationId
        }
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/templates?organizationId=${effectiveOrganizationId}`] });
      setDialogOpen(false);
      setTemplateName("");
      setTemplateCategory(TEMPLATE_CATEGORIES.MEETING);
      setTemplateContent("");
      toast({
        title: "Success",
        description: "Template created successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create template",
        variant: "destructive",
      });
    }
  });

  // Filter templates based on search and category
  const filterTemplates = (templates: Template[]) => {
    return templates.filter(template => {
      const matchesSearch = searchQuery === "" || 
        template.name.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = selectedCategory === "all" || 
        template.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  };

  // Handle creating a new template
  const handleCreateTemplate = () => {
    if (!templateName.trim() || !templateContent.trim()) {
      toast({
        title: "Validation Error",
        description: "Template name and content are required",
        variant: "destructive",
      });
      return;
    }

    createTemplate.mutate();
  };

  // Get category label
  const getCategoryLabel = (category: string) => {
    const categoryMap: Record<string, string> = {
      [TEMPLATE_CATEGORIES.MEETING]: 'Meeting',
      [TEMPLATE_CATEGORIES.PAGE]: 'Page',
      [TEMPLATE_CATEGORIES.SCORECARD]: 'Scorecard',
      [TEMPLATE_CATEGORIES.COACHING]: 'Coaching',
      [TEMPLATE_CATEGORIES.ONBOARDING]: 'Onboarding'
    };
    
    return categoryMap[category] || category;
  };

  // Mock data for development
  const mockTemplates: Template[] = [
    {
      id: "1",
      organizationId: effectiveOrganizationId,
      name: "Level 10 Meeting Agenda",
      category: TEMPLATE_CATEGORIES.MEETING,
      content: JSON.stringify({
        sections: [
          { title: 'Segue (5 min)', duration: 5 },
          { title: 'Scorecard (5 min)', duration: 5 },
          { title: 'Rock Review (5 min)', duration: 5 },
          { title: 'Customer/Employee Headlines (5 min)', duration: 5 },
          { title: 'To-Do List (5 min)', duration: 5 },
          { title: 'IDS (30 min)', duration: 30 },
          { title: 'Conclude (5 min)', duration: 5 }
        ]
      }),
      createdAt: new Date(Date.now() - 30 * 86400000),
      updatedAt: new Date(Date.now() - 15 * 86400000)
    },
    {
      id: "2",
      organizationId: effectiveOrganizationId,
      name: "Quarterly Planning Template",
      category: TEMPLATE_CATEGORIES.MEETING,
      content: JSON.stringify({
        sections: [
          { title: 'Vision Review (30 min)', duration: 30 },
          { title: 'Rocks (60 min)', duration: 60 },
          { title: 'Issues List (60 min)', duration: 60 },
          { title: 'Quarterly Goals (30 min)', duration: 30 }
        ]
      }),
      createdAt: new Date(Date.now() - 90 * 86400000),
      updatedAt: new Date(Date.now() - 90 * 86400000)
    },
    {
      id: "3",
      organizationId: effectiveOrganizationId,
      name: "Coaching Session Guide",
      category: TEMPLATE_CATEGORIES.COACHING,
      content: JSON.stringify({
        sections: [
          { title: 'Performance Review', questions: ['What went well?', 'What could be improved?'] },
          { title: 'Goal Setting', questions: ['What are your goals for next month?'] },
          { title: 'Action Items', questions: ['What steps will you take?'] }
        ]
      }),
      createdAt: new Date(Date.now() - 60 * 86400000),
      updatedAt: new Date(Date.now() - 60 * 86400000)
    },
    {
      id: "4",
      organizationId: effectiveOrganizationId,
      name: "New Hire Onboarding",
      category: TEMPLATE_CATEGORIES.ONBOARDING,
      content: JSON.stringify({
        sections: [
          { title: 'Welcome', duration: 30 },
          { title: 'Company Overview', duration: 60 },
          { title: 'Role Training', duration: 120 },
          { title: 'Systems Access', duration: 60 }
        ]
      }),
      createdAt: new Date(Date.now() - 120 * 86400000),
      updatedAt: new Date(Date.now() - 120 * 86400000)
    },
    {
      id: "5",
      organizationId: effectiveOrganizationId,
      name: "COVID-19 Safety Protocol",
      category: TEMPLATE_CATEGORIES.PAGE,
      content: JSON.stringify({
        content: "# COVID-19 Safety Protocol\n\n## Patient Guidelines\n\n- Masks required for all patients\n- Temperature checks at entrance\n- Social distancing in waiting areas\n\n## Staff Guidelines\n\n- N95 masks required for all staff\n- Daily symptom checks\n- Regular sanitization of equipment"
      }),
      createdAt: new Date(Date.now() - 180 * 86400000),
      updatedAt: new Date(Date.now() - 30 * 86400000)
    },
    {
      id: "6",
      organizationId: effectiveOrganizationId,
      name: "Team Member Scorecard",
      category: TEMPLATE_CATEGORIES.SCORECARD,
      content: JSON.stringify({
        metrics: [
          { name: 'Patient Satisfaction', target: '4.8/5' },
          { name: 'Appointments Completed', target: '35/week' },
          { name: 'Documentation Timeliness', target: '100%' },
          { name: 'Continuing Education', target: '4 hours/month' }
        ]
      }),
      createdAt: new Date(Date.now() - 45 * 86400000),
      updatedAt: new Date(Date.now() - 45 * 86400000)
    }
  ];

  const displayTemplates = templates || mockTemplates;
  const filteredTemplates = filterTemplates(displayTemplates);

  // Get template icon based on category
  const getTemplateIcon = (category: string) => {
    switch (category) {
      case TEMPLATE_CATEGORIES.MEETING:
        return 'mdi-calendar-text';
      case TEMPLATE_CATEGORIES.PAGE:
        return 'mdi-file-document-outline';
      case TEMPLATE_CATEGORIES.SCORECARD:
        return 'mdi-chart-box-outline';
      case TEMPLATE_CATEGORIES.COACHING:
        return 'mdi-account-supervisor';
      case TEMPLATE_CATEGORIES.ONBOARDING:
        return 'mdi-account-plus';
      default:
        return 'mdi-file-outline';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between mb-6">
        <div className="flex items-center space-x-2 mb-4 md:mb-0">
          <h1 className="text-2xl font-medium">Templates</h1>
          <span 
            className="mdi mdi-information-outline text-neutral-500 dark:text-neutral-400 cursor-help" 
            title="Standardized templates for meetings, documents, and processes"
          ></span>
        </div>
        
        <div className="flex items-center space-x-4">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <span className="mdi mdi-plus mr-2"></span>
                New Template
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Create New Template</DialogTitle>
                <DialogDescription>
                  Create standardized templates for your organization
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <label htmlFor="name" className="text-sm font-medium">
                    Template Name
                  </label>
                  <Input
                    id="name"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Enter template name"
                  />
                </div>
                
                <div className="grid gap-2">
                  <label htmlFor="category" className="text-sm font-medium">
                    Category
                  </label>
                  <Select
                    value={templateCategory}
                    onValueChange={setTemplateCategory}
                  >
                    <SelectTrigger id="category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(TEMPLATE_CATEGORIES).map((category) => (
                        <SelectItem key={category} value={category}>
                          {getCategoryLabel(category)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid gap-2">
                  <label htmlFor="content" className="text-sm font-medium">
                    Template Content
                  </label>
                  <Textarea
                    id="content"
                    value={templateContent}
                    onChange={(e) => setTemplateContent(e.target.value)}
                    placeholder="Enter template content (use Markdown for formatting)"
                    rows={10}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateTemplate}
                  disabled={createTemplate.isPending}
                >
                  {createTemplate.isPending ? "Creating..." : "Create Template"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Category Tabs */}
      <div className="flex flex-col sm:flex-row justify-between mb-6 space-y-4 sm:space-y-0">
        <div className="relative max-w-md w-full">
          <Input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pr-10"
          />
          <span className="mdi mdi-magnify absolute right-3 top-2.5 text-neutral-400"></span>
        </div>
        
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full sm:w-auto">
          <TabsList className="overflow-x-auto">
            <TabsTrigger value="all">All</TabsTrigger>
            {Object.values(TEMPLATE_CATEGORIES).map((category) => (
              <TabsTrigger key={category} value={category}>
                {getCategoryLabel(category)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          [...Array(6)].map((_, index) => (
            <Skeleton key={index} className="h-48 w-full" />
          ))
        ) : (
          filteredTemplates.map((template) => (
            <Card key={template.id} className="overflow-hidden">
              <CardHeader className="p-4 pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex items-start space-x-2">
                    <span className={`mdi ${getTemplateIcon(template.category)} text-primary text-xl mt-0.5`}></span>
                    <div>
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      <p className="text-xs text-neutral-500 mt-1">
                        {getCategoryLabel(template.category)}<TemplateUpdatedDate template={template} />
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline">{getCategoryLabel(template.category)}</Badge>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <div className="text-sm text-neutral-600 dark:text-neutral-300">
                  {template.category === TEMPLATE_CATEGORIES.MEETING && template.content && typeof template.content === 'string' ? (
                    <div>
                      {(JSON.parse(template.content) as any).sections?.slice(0, 3).map((section: any, index: number) => (
                        <div key={index} className="flex justify-between items-center mb-2">
                          <span>{section.title}</span>
                          {section.duration && <span className="text-xs text-neutral-500">{section.duration} min</span>}
                        </div>
                      ))}
                      {(JSON.parse(template.content) as any).sections?.length > 3 && (
                        <p className="text-center text-neutral-500 text-xs mt-2">
                          +{(JSON.parse(template.content) as any).sections.length - 3} more sections
                        </p>
                      )}
                    </div>
                  ) : null}
                  
                  {template.category === TEMPLATE_CATEGORIES.SCORECARD && template.content && typeof template.content === 'string' ? (
                    <div>
                      {(JSON.parse(template.content) as any).metrics?.slice(0, 3).map((metric: any, index: number) => (
                        <div key={index} className="flex justify-between items-center mb-2">
                          <span>{metric.name}</span>
                          {metric.target && <span className="text-xs text-neutral-500">Target: {metric.target}</span>}
                        </div>
                      ))}
                      {(JSON.parse(template.content) as any).metrics?.length > 3 && (
                        <p className="text-center text-neutral-500 text-xs mt-2">
                          +{(JSON.parse(template.content) as any).metrics.length - 3} more metrics
                        </p>
                      )}
                    </div>
                  ) : null}
                  
                  {template.category === TEMPLATE_CATEGORIES.PAGE && template.content && typeof template.content === 'string' ? (
                    <div className="line-clamp-3">
                      {(JSON.parse(template.content) as any).content}
                    </div>
                  ) : null}
                  
                  {(template.category === TEMPLATE_CATEGORIES.COACHING || template.category === TEMPLATE_CATEGORIES.ONBOARDING) && template.content && typeof template.content === 'string' ? (
                    <div>
                      {(JSON.parse(template.content) as any).sections?.slice(0, 3).map((section: any, index: number) => (
                        <div key={index} className="mb-2">
                          <div className="font-medium">{section.title}</div>
                          {section.questions && (
                            <div className="text-xs text-neutral-500 pl-3">
                              {section.questions.length} question{section.questions.length !== 1 ? 's' : ''}
                            </div>
                          )}
                          {section.duration && (
                            <div className="text-xs text-neutral-500 pl-3">
                              {section.duration} minutes
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </CardContent>
              <CardFooter className="p-4 pt-0 flex justify-between">
                <Button variant="outline" size="sm">
                  <span className="mdi mdi-pencil mr-1"></span>
                  Edit
                </Button>
                <Button size="sm">
                  <span className="mdi mdi-content-copy mr-1"></span>
                  Use Template
                </Button>
              </CardFooter>
            </Card>
          ))
        )}
      </div>

      {filteredTemplates.length === 0 && !isLoading && (
        <Card className="p-8 text-center">
          <h3 className="text-lg font-medium mb-2">No Templates Found</h3>
          <p className="text-neutral-500 dark:text-neutral-400 mb-4">
            {searchQuery ? 
              `No templates match your search for "${searchQuery}"` : 
              "No templates have been created yet."}
          </p>
          <Button onClick={() => setDialogOpen(true)}>
            <span className="mdi mdi-plus mr-2"></span>
            Create Template
          </Button>
        </Card>
      )}
    </div>
  );
}
